import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { StarRatingDisplay } from "@/components/StarRating";
import { LogOut, Trash2, Check, X, Star, Plus, Calendar } from "lucide-react";

export default function AdminDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [slots, setSlots] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("ope_admin_token")) { navigate("/admin/login"); return; }
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const [i, m, r, s, c] = await Promise.all([
        api.get("/inquiries"), api.get("/contact-messages"), api.get("/admin/reviews"),
        api.get("/admin/slots"), api.get("/admin/consultations"),
      ]);
      setInquiries(i.data); setMessages(m.data); setReviews(r.data); setSlots(s.data); setConsultations(c.data);
    } catch (err) {
      if (err?.response?.status === 401) { localStorage.removeItem("ope_admin_token"); navigate("/admin/login"); }
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem("ope_admin_token"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-border bg-secondary/40 px-6 lg:px-10 py-6 flex items-center justify-between sticky top-0 z-40">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-gold">Admin</p>
          <h1 className="font-heading text-3xl font-light">Our Promise Events</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase border-b border-ink pb-1 hover:text-gold hover:border-gold">
          <LogOut size={14} /> {t("admin.logout")}
        </button>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <Tabs defaultValue="inquiries">
          <TabsList className="bg-secondary/40 border border-border rounded-none mb-8 flex-wrap h-auto">
            {[
              ["inquiries", `${t("admin.tabs.inquiries")} (${inquiries.length})`],
              ["messages", `${t("admin.tabs.messages")} (${messages.length})`],
              ["reviews", `${t("admin.tabs.reviews")} (${reviews.length})`],
              ["slots", `${t("admin.tabs.slots")} (${slots.length})`],
              ["consultations", `${t("admin.tabs.consultations")} (${consultations.length})`],
              ["gcal", "Google Kalender"],
              ["content", t("admin.tabs.content")],
            ].map(([v, label]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-cream px-5 py-2.5 text-[11px] tracking-widest uppercase">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="inquiries"><InquiriesTab items={inquiries} reload={loadAll} loading={loading} /></TabsContent>
          <TabsContent value="messages"><MessagesTab items={messages} reload={loadAll} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab items={reviews} reload={loadAll} /></TabsContent>
          <TabsContent value="slots"><SlotsTab items={slots} reload={loadAll} /></TabsContent>
          <TabsContent value="consultations"><ConsultationsTab items={consultations} /></TabsContent>
          <TabsContent value="gcal"><GoogleCalendarTab /></TabsContent>
          <TabsContent value="content"><ContentTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Empty() {
  const { t } = useLanguage();
  return <p className="text-ink-muted" data-testid="admin-empty">{t("admin.empty")}</p>;
}

function InquiriesTab({ items, reload, loading }) {
  const { t } = useLanguage();
  const statuses = ["neu", "in_abklaerung", "angebot_gesendet", "bestaetigt", "abgelehnt"];
  const update = async (id, status) => { try { await api.patch(`/inquiries/${id}/status`, { status }); toast.success(t("admin.saved")); reload(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/inquiries/${id}`); reload(); } catch { toast.error("Failed"); } };
  if (loading) return <p className="text-ink-muted">…</p>;
  if (!items.length) return <Empty />;
  return (
    <div className="space-y-4">
      {items.map((inq) => (
        <div key={inq.id} data-testid={`inquiry-${inq.id}`} className="bg-secondary/40 border border-border p-6 grid lg:grid-cols-6 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-muted">{inq.wedding_date}</p>
            <p className="font-heading text-lg">{inq.event_type}</p>
            {inq.package && <p className="text-xs uppercase tracking-wider text-gold mt-1">{inq.package}</p>}
          </div>
          <div className="lg:col-span-2">
            <p className="font-medium">{inq.name}</p>
            <p className="text-sm text-ink-muted">{inq.email}</p>
            <p className="text-sm text-ink-muted">{inq.phone}</p>
            <p className="text-sm">{inq.location} · {inq.guests} · {inq.budget || "—"}</p>
            {inq.services?.length > 0 && <p className="text-xs text-ink-muted mt-1">{inq.services.join(", ")}</p>}
          </div>
          <div className="lg:col-span-2">
            <p className="text-sm text-ink-muted italic">{inq.message || "—"}</p>
          </div>
          <div className="flex flex-col gap-2">
            <Select value={inq.status} onValueChange={(v) => update(inq.id, v)}>
              <SelectTrigger className="rounded-none border-border h-9 text-xs" data-testid={`inquiry-status-${inq.id}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-cream">
                {statuses.map((s) => <SelectItem key={s} value={s} data-testid={`inquiry-status-opt-${s}`}>{t(`admin.inquiryStatus.${s}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => del(inq.id)} data-testid={`inquiry-delete-${inq.id}`} className="text-xs text-ink-muted hover:text-destructive flex items-center gap-1 self-start"><Trash2 size={13} /> {t("admin.delete")}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessagesTab({ items, reload }) {
  const { t } = useLanguage();
  const update = async (id, status) => { try { await api.patch(`/contact-messages/${id}/status`, { status }); reload(); } catch { toast.error("Failed"); } };
  if (!items.length) return <Empty />;
  return (
    <div className="space-y-4">
      {items.map((m) => (
        <div key={m.id} data-testid={`message-${m.id}`} className="bg-secondary/40 border border-border p-6 grid lg:grid-cols-4 gap-4">
          <div><p className="font-medium">{m.name}</p><p className="text-sm text-ink-muted">{m.email}</p>{m.phone && <p className="text-sm text-ink-muted">{m.phone}</p>}</div>
          <div className="lg:col-span-2"><p className="text-sm">{m.message}</p></div>
          <div>
            <Select value={m.status} onValueChange={(v) => update(m.id, v)}>
              <SelectTrigger className="rounded-none border-border h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-cream">
                <SelectItem value="neu">{t("admin.messageStatus.neu")}</SelectItem>
                <SelectItem value="gelesen">{t("admin.messageStatus.gelesen")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsTab({ items, reload }) {
  const { t } = useLanguage();
  const act = async (id, status) => { try { await api.patch(`/reviews/${id}/status`, { status }); toast.success(t("admin.saved")); reload(); } catch { toast.error("Failed"); } };
  const highlight = async (id) => { try { await api.patch(`/reviews/${id}/highlight`); reload(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await api.delete(`/reviews/${id}`); reload(); } catch { toast.error("Failed"); } };
  if (!items.length) return <Empty />;
  const badge = { pending: "border-gold text-ink bg-gold/10", approved: "border-ink bg-ink text-cream", rejected: "border-border text-ink-muted" };
  return (
    <div className="space-y-4">
      {items.map((r) => (
        <div key={r.id} data-testid={`review-${r.id}`} className="bg-secondary/40 border border-border p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3 mb-2">
                <StarRatingDisplay value={r.rating} />
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${badge[r.status]}`} data-testid={`review-status-${r.id}`}>{t(`admin.reviewStatus.${r.status}`)}</span>
                {r.highlight && <Star size={14} className="fill-gold text-gold" />}
              </div>
              <p className="italic text-ink/80 mb-2">« {r.text} »</p>
              <p className="text-sm text-ink-muted uppercase tracking-wider">{r.name} · {r.event_type} · {r.email}</p>
            </div>
            {r.photos?.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {r.photos.map((p, i) => <img key={i} src={p} alt="" className="w-16 h-16 object-cover border border-border" />)}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4 pt-4 border-t border-border">
            <button onClick={() => act(r.id, "approved")} data-testid={`review-approve-${r.id}`} className="text-xs flex items-center gap-1 text-ink hover:text-gold"><Check size={14} /> {t("admin.approve")}</button>
            <button onClick={() => act(r.id, "rejected")} data-testid={`review-reject-${r.id}`} className="text-xs flex items-center gap-1 text-ink-muted hover:text-destructive"><X size={14} /> {t("admin.reject")}</button>
            <button onClick={() => highlight(r.id)} className="text-xs flex items-center gap-1 text-ink-muted hover:text-gold"><Star size={14} /> {t("admin.highlight")}</button>
            <button onClick={() => del(r.id)} className="text-xs flex items-center gap-1 text-ink-muted hover:text-destructive ml-auto"><Trash2 size={13} /> {t("admin.delete")}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SlotsTab({ items, reload }) {
  const { t } = useLanguage();
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!date || !time) return;
    try { await api.post("/admin/slots", { date, time }); toast.success(t("admin.saved")); setTime(""); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/admin/slots/${id}`); reload(); } catch { toast.error("Failed"); } };
  return (
    <div>
      <form onSubmit={add} data-testid="slot-form" className="flex flex-wrap items-end gap-4 bg-secondary/40 border border-border p-6 mb-6">
        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("admin.slotDate")}</label>
          <input type="date" data-testid="slot-date" value={date} onChange={(e) => setDate(e.target.value)} className="border-b border-border bg-transparent py-2 focus:outline-none focus:border-ink" />
        </div>
        <div>
          <label className="text-xs tracking-[0.2em] uppercase text-ink-muted mb-2 block">{t("admin.slotTime")}</label>
          <input type="time" data-testid="slot-time" value={time} onChange={(e) => setTime(e.target.value)} className="border-b border-border bg-transparent py-2 focus:outline-none focus:border-ink" />
        </div>
        <button type="submit" data-testid="slot-add" className="flex items-center gap-2 bg-ink text-cream px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-gold transition-colors"><Plus size={14} /> {t("admin.addSlot")}</button>
      </form>
      {!items.length ? <Empty /> : (
        <div className="flex flex-wrap gap-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`slot-${s.id}`} className={`flex items-center gap-3 px-4 py-3 border ${s.is_booked ? "border-ink bg-ink text-cream" : "border-border bg-secondary/40"}`}>
              <span className="text-sm">{s.date} · {s.time}</span>
              <span className="text-[10px] uppercase tracking-wider">{s.is_booked ? t("admin.booked") : t("admin.free")}</span>
              {!s.is_booked && <button onClick={() => del(s.id)} className="hover:text-destructive"><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsultationsTab({ items }) {
  if (!items.length) return <Empty />;
  return (
    <div className="space-y-4">
      {items.map((c) => (
        <div key={c.id} data-testid={`consultation-${c.id}`} className="bg-secondary/40 border border-border p-6 grid lg:grid-cols-4 gap-4">
          <div><p className="font-heading text-lg">{c.date}</p><p className="text-sm text-gold">{c.time}</p></div>
          <div className="lg:col-span-2"><p className="font-medium">{c.name}</p><p className="text-sm text-ink-muted">{c.email} · {c.phone}</p></div>
          <div><p className="text-sm text-ink-muted italic">{c.message || "—"}</p></div>
        </div>
      ))}
    </div>
  );
}

function ContentTab() {
  const { t } = useLanguage();
  const { reload: reloadContent } = useContent();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/content").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <p className="text-ink-muted">…</p>;

  const save = async (section) => {
    try { await api.put(`/content/${section}`, { data: data[section] }); toast.success(t("admin.saved")); reloadContent(); }
    catch { toast.error("Failed"); }
  };
  const setPkg = (key, field, val) => setData((d) => ({ ...d, packages: { ...d.packages, [key]: { ...d.packages[key], [field]: val } } }));
  const setContact = (field, val) => setData((d) => ({ ...d, contact: { ...d.contact, [field]: val } }));

  return (
    <div className="space-y-10">
      <p className="text-sm text-ink-muted italic">{t("admin.contentHint")}</p>

      {/* Packages prices */}
      <section className="bg-secondary/40 border border-border p-6">
        <h3 className="font-heading text-2xl mb-5">Packages</h3>
        <div className="grid sm:grid-cols-3 gap-6">
          {["bronze", "silber", "gold"].map((k) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-wider text-ink-muted block mb-2">{k}</label>
              <input value={data.packages[k]?.price || ""} data-testid={`content-price-${k}`} onChange={(e) => setPkg(k, "price", e.target.value)} className="w-full border-b border-border bg-transparent py-2 focus:outline-none focus:border-ink mb-2" />
              <label className="flex items-center gap-2 text-xs text-ink-muted"><input type="checkbox" checked={!!data.packages[k]?.highlight} onChange={(e) => setPkg(k, "highlight", e.target.checked)} /> Highlight</label>
            </div>
          ))}
        </div>
        <button onClick={() => save("packages")} data-testid="content-save-packages" className="mt-5 bg-ink text-cream px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-gold transition-colors">{t("admin.save")}</button>
      </section>

      {/* Contact */}
      <section className="bg-secondary/40 border border-border p-6">
        <h3 className="font-heading text-2xl mb-5">Kontakt</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          {["email", "phone", "instagram", "instagram_handle", "address"].map((f) => (
            <div key={f}>
              <label className="text-xs uppercase tracking-wider text-ink-muted block mb-2">{f}</label>
              <input value={data.contact[f] || ""} data-testid={`content-contact-${f}`} onChange={(e) => setContact(f, e.target.value)} className="w-full border-b border-border bg-transparent py-2 focus:outline-none focus:border-ink" />
            </div>
          ))}
        </div>
        <button onClick={() => save("contact")} data-testid="content-save-contact" className="mt-5 bg-ink text-cream px-6 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-gold transition-colors">{t("admin.save")}</button>
      </section>
    </div>
  );
}

function GoogleCalendarTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/gcal/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Status konnte nicht geladen werden");
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/gcal/authorize");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error("Konnte Autorisierungs-URL nicht laden");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Google Kalender wirklich trennen? Bereits synchronisierte Termine bleiben im Kalender.")) return;
    setBusy(true);
    try {
      await api.post("/admin/gcal/disconnect");
      toast.success("Verbindung getrennt");
      await load();
    } catch (e) {
      toast.error("Trennen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const syncExisting = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/gcal/sync-existing");
      toast.success(`Synchronisiert: ${r.data.slots_synced} Slots · ${r.data.consultations_synced} Termine`);
    } catch (e) {
      toast.error("Sync fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <p className="text-ink-muted">Wird geladen…</p>;

  return (
    <div className="space-y-6" data-testid="gcal-tab">
      <section className="bg-secondary/40 border border-border p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center border border-gold/40">
            <Calendar size={20} className="text-gold" />
          </div>
          <div>
            <h3 className="font-heading text-2xl font-light">Google Kalender</h3>
            <p className="text-sm text-ink-muted mt-1">Synchronisiere freie Slots und gebuchte Kennenlerngespräche automatisch mit deinem Google Kalender.</p>
          </div>
        </div>

        {!status.configured && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 p-4">
            Google OAuth ist auf dem Server nicht konfiguriert. Bitte <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code> und <code>PUBLIC_BASE_URL</code> in der <code>backend/.env</code> setzen.
          </p>
        )}

        {status.configured && !status.connected && (
          <div>
            <p className="text-sm text-ink-muted mb-5">
              Nach dem Klick öffnet sich Google und du autorisierst den Zugriff. Der eingerichtete Test-User <code>bambefoot@gmail.com</code> kann sich verbinden. Später kannst du auf das Konto der Planerin wechseln.
            </p>
            <button
              onClick={connect}
              disabled={busy}
              data-testid="gcal-connect-btn"
              className="inline-flex items-center gap-3 bg-ink text-cream px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-gold transition-colors disabled:opacity-50"
            >
              <Calendar size={16} /> Google Kalender verbinden
            </button>
          </div>
        )}

        {status.configured && status.connected && (
          <div>
            <div className="bg-cream border border-border p-5 mb-6">
              <p className="text-xs uppercase tracking-widest text-gold mb-2">Verbunden</p>
              <p className="font-heading text-xl mb-1" data-testid="gcal-connected-email">{status.email || "Google Kalender aktiv"}</p>
              {status.connected_at && (
                <p className="text-xs text-ink-muted">Seit {new Date(status.connected_at).toLocaleString("de-CH")}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={syncExisting}
                disabled={busy}
                data-testid="gcal-sync-btn"
                className="inline-flex items-center gap-2 border border-gold text-gold px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-cream transition-colors disabled:opacity-50"
              >
                Bestehende synchronisieren
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                data-testid="gcal-disconnect-btn"
                className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-ink hover:text-cream transition-colors disabled:opacity-50"
              >
                Verbindung trennen
              </button>
            </div>
            <p className="text-xs text-ink-muted mt-4">
              Zum späteren Wechsel auf das Kundinnen-Konto: hier trennen → einmalig in Google unter <a className="underline" target="_blank" rel="noopener noreferrer" href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</a> den Zugriff für "Our Promise Events" entfernen → dann erneut verbinden.
            </p>
          </div>
        )}
      </section>

      <section className="bg-cream border border-border p-8">
        <h4 className="font-heading text-xl font-light mb-3">Wie funktioniert die Sync?</h4>
        <ul className="text-sm text-ink-muted space-y-2 list-disc pl-5">
          <li>Beim Anlegen eines neuen Slots wird ein "Verfügbarer Slot"-Eintrag im Google Kalender erstellt (blau, transparent).</li>
          <li>Bei Buchung eines Kennenlerngesprächs wird der freie Slot ersetzt durch einen bestätigten Termin (rot) mit Namen, E-Mail und Nachricht des Kunden.</li>
          <li>Der Kunde bekommt automatisch die Kalendereinladung per E-Mail.</li>
          <li>Beim Löschen eines Slots wird der Kalender-Eintrag ebenfalls entfernt.</li>
          <li>Ältere Slots/Termine kannst du per "Bestehende synchronisieren" nachtragen.</li>
        </ul>
      </section>
    </div>
  );
}

